import { TriangleAlert } from "lucide-react";
import type { WriteRejection } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import { dateTime } from "@/modules/orders/format";
import { describeRejection } from "@/modules/orders/writeRejection";

/**
 * Every `WriteRejection` kind rendered as what it actually is, shared by the
 * tag, note and cancel edits on the order detail (#60, #167).
 *
 * `conflict` and `invalid` get bespoke treatment because they carry
 * structured data a user can act on — what changed in Shopify and a reload
 * affordance, a per-field message — that a flattened string would throw
 * away. `blocked` and `channel_rejected` carry the channel's own words
 * verbatim, and `forbidden` cannot occur from these actions today, but the
 * union still has no `default`: a new kind fails this file at compile time
 * instead of silently rendering nothing.
 *
 * The form that shows this keeps what the user typed: a refusal changes
 * nothing, locally or in Shopify, so their input is still the thing to fix
 * and resend.
 */
export function RejectionNotice({
  rejection,
  onReload,
}: {
  rejection: WriteRejection;
  onReload: () => void;
}) {
  if (rejection.kind === "conflict") {
    return (
      <div
        role="alert"
        className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-1.5 rounded-md border p-2 text-xs"
      >
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <div className="min-w-0 space-y-1.5">
          <p>{rejection.message}</p>
          {rejection.changes && rejection.changes.length > 0 ? (
            <dl className="text-foreground grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              {rejection.changes.map((change) => (
                <div key={change.field} className="contents">
                  <dt className="text-muted-foreground">{change.field}</dt>
                  <dd className="break-words">
                    <span className="text-muted-foreground line-through">
                      {change.loaded ?? "—"}
                    </span>{" "}
                    → {change.current ?? "—"}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          <p className="text-muted-foreground">
            Changed in Shopify {dateTime(rejection.currentUpdatedAt)}.
          </p>
          <Button type="button" size="xs" variant="outline" onClick={onReload}>
            Reload
          </Button>
        </div>
      </div>
    );
  }

  if (rejection.kind === "invalid") {
    return (
      <p role="alert" className="text-destructive text-xs">
        {rejection.message}
        {rejection.fields
          ? " " +
            Object.entries(rejection.fields)
              .map(([field, problem]) => `${field}: ${problem}`)
              .join("; ")
          : null}
      </p>
    );
  }

  return (
    <div
      role="alert"
      className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-1.5 rounded-md border p-2 text-xs"
    >
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <div className="min-w-0 space-y-0.5">
        {rejection.kind === "channel_rejected" ? (
          <p className="font-medium">
            Shopify refused this change. Nothing was changed.
          </p>
        ) : null}
        <p className="break-words">{describeRejection(rejection)}</p>
      </div>
    </div>
  );
}
